import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { ModalType } from 'shared/domain/projectEligibility'

/**
 * Page-level actions a section may trigger but does not own.
 *
 * The login modal lives in `BaseLayout` and is driven by state held in `StoPage`, so a
 * section cannot open it by itself. This carries just that capability down, instead of
 * threading `setIsLoginModalOpen` through every section as a prop — sections stay
 * prop-free (architecture decision 28) and the modal keeps working on the STO page
 * rather than bouncing the visitor to the homepage to sign in.
 */
export interface StoPageActions {
  openLoginModal: () => void
  /** Opens the modal that completes an eligibility step (private / professional investor). */
  openStepModal: (modal: ModalType) => void
}

const StoPageActionsContext = createContext<StoPageActions>({
  // A no-op default keeps sections renderable in isolation (tests, storybook) without
  // forcing every consumer to null-check.
  openLoginModal: () => {},
  openStepModal: () => {},
})

export const useStoPageActions = (): StoPageActions => useContext(StoPageActionsContext)

export const StoPageActionsProvider = ({
  openLoginModal,
  openStepModal,
  children,
}: StoPageActions & { children: ReactNode }) => {
  const value = useMemo(() => ({ openLoginModal, openStepModal }), [openLoginModal, openStepModal])

  return <StoPageActionsContext.Provider value={value}>{children}</StoPageActionsContext.Provider>
}
